import React, { useState } from 'react';
import moment from 'moment';
import 'moment/locale/pt';
import ReactHtmlParser from 'react-html-parser';
import { useClient, useMutation } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { FiHeart, FiMessageCircle, FiTrash2 } from 'react-icons/fi';
import { MdOutlinePushPin, MdPushPin } from 'react-icons/md';
import { cn } from '@/lib/utils';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Loader } from '@/components/custom/loader';
import { MagicLink } from '@/components/custom/magic-link';
import { useSession } from '@/hooks/useSession';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import { confirm } from '@/lib/confirm';
import api from '@/services/api';

export function PostCard({ post, setPosts }) {
  const client = useClient();
  const { session } = useSession();
  const authorityLevel = useAuthorityLevel();
  const [addUserInteraction] = useMutation('user_interactions');
  const [addComment] = useMutation('comments');

  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState(null);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [isDeletingPost, setIsDeletingPost] = useState(false);

  const updatePost = changes => {
    setPosts(prev => prev.map(p => (p.id === post.id ? { ...p, ...changes } : p)));
  };

  const deletePost = async () => {
    const confirmed = await confirm({
      title: 'Deseja mesmo excluir esta postagem?',
      description: 'Esta ação não poderá ser desfeita',
      confirmText: 'Sim',
      cancelText: 'Não'
    });
    if (!confirmed) return;

    setIsDeletingPost(true);

    try {
      await api.delete(`posts/${post.id}`);
      setPosts(prev => prev.filter(p => p.id !== post.id));
      toast.success('Postagem excluída com sucesso');
    } catch (error) {
      toast.error(`Erro ao excluir postagem: ${error}`);
      console.error(error);
      setIsDeletingPost(false);
    }
  };

  const toggleFixPost = async () => {
    const pinned = !post.pinned;

    try {
      const { data, error } = await client.mutate(`posts/${post.id}`, { pinned });

      if (error) {
        toast.error('Erro ao fixar postagem');
        return;
      }

      if (pinned) {
        setPosts(prev => [data, ...prev.filter(p => p.id !== data.id)]);
      } else {
        updatePost({ pinned: false });
      }
    } catch (error) {
      toast.error('Erro ao fixar postagem');
    }
  };

  const toggleLike = async () => {
    if (post['like-id']) {
      try {
        await api.delete(`user_interactions/${post['like-id']}`);
        updatePost({ 'like-id': null, 'likes-count': post['likes-count'] - 1 });
      } catch (error) {
        toast.error('Erro ao curtir publicação');
      }
    } else {
      const response = await addUserInteraction({
        action: 'liked',
        'interactionable-type': 'Post',
        'interactionable-id': post.id
      });

      if (response.error) {
        toast.error(response.error.title);
      } else {
        updatePost({ 'like-id': response.data.id, 'likes-count': post['likes-count'] + 1 });
      }
    }
  };

  const toggleComments = async () => {
    const willOpen = !commentsOpen;
    setCommentsOpen(willOpen);

    if (willOpen && comments === null) {
      setLoadingComments(true);
      const { data, error } = await client.fetch(['posts', post.id]);

      if (error) {
        toast.error('Erro ao buscar comentários');
      } else {
        setComments((data?.comments || []).filter(comment => comment['comment-parent-id'] === null));
      }
      setLoadingComments(false);
    }
  };

  const submitComment = async e => {
    e.preventDefault();
    if (!commentText.trim()) return;

    const response = await addComment({ body: commentText.trim(), post_id: post.id });

    if (response.error) {
      toast.error(response.error.title);
    } else {
      const { data, error } = await client.fetch(['posts', post.id], { force: true });

      if (!error) {
        setComments((data?.comments || []).filter(c => c['comment-parent-id'] === null));
      }

      updatePost({ 'comments-count': post['comments-count'] + 1 });
      setCommentText('');
    }
  };

  const deleteComment = async comment => {
    const confirmed = await confirm({
      title: 'Deseja mesmo excluir este comentário?',
      description: 'Esta ação não poderá ser desfeita',
      confirmText: 'Sim',
      cancelText: 'Não'
    });
    if (!confirmed) return;

    setDeletingCommentId(comment.id);

    try {
      await api.delete(`comments/${comment.id}`);
      setComments(prev => prev.filter(c => c.id !== comment.id));
      updatePost({ 'comments-count': post['comments-count'] - 1 });
      toast.success('Comentário excluído com sucesso');
    } catch (error) {
      toast.error(`Erro ao excluir comentário: ${error}`);
      console.error(error);
    } finally {
      setDeletingCommentId(null);
    }
  };

  return (
    <div className={cn('flex flex-col gap-3 rounded-2xl border border-neutral-lightest bg-white p-6 shadow-card transition-shadow duration-150 hover:shadow-card-hover', isDeletingPost && 'pointer-events-none opacity-50')}>
      <div className="flex items-center gap-3">
        <AvatarWrapper
          size="sm"
          src={post?.user?.image?.url}
          text={post?.user?.name}
        />
        <div>
          <p className="mb-0 text-sm font-extrabold text-neutral-900">{post?.user?.name}</p>
          <p className="mb-0 text-xs text-neutral-dark">{moment(post['created-at']).fromNow()}</p>
        </div>
      </div>

      <div className="mb-0 whitespace-pre-line text-base text-neutral-900">{ReactHtmlParser(post?.body)}</div>

      {post['magic-links'] &&
        post['magic-links'].map(link => (
          <MagicLink
            key={link?.id}
            variant="post"
            magicLink={link}
            className="mt-0 w-full md:ml-0 md:w-full"
          />
        ))}

      <div className="flex items-center gap-4 border-t border-neutral-lightest pt-2">
        <button
          type="button"
          onClick={toggleLike}
          aria-pressed={!!post['like-id']}
          className={cn('flex items-center gap-1.5 text-xs font-bold transition-colors', post['like-id'] ? 'text-mission' : 'text-neutral-dark')}
        >
          <FiHeart className={cn('h-4 w-4', post['like-id'] && 'fill-current')} />
          {post['likes-count']}
        </button>

        <button
          type="button"
          onClick={toggleComments}
          className="flex items-center gap-1.5 text-xs font-bold text-neutral-dark"
        >
          <FiMessageCircle className="h-4 w-4" />
          {post['comments-count']}
        </button>

        {authorityLevel !== 'student' && (
          <button
            type="button"
            onClick={toggleFixPost}
            aria-pressed={!!post.pinned}
            aria-label={post.pinned ? 'Desafixar postagem' : 'Fixar postagem'}
            title={post.pinned ? 'Desafixar postagem' : 'Fixar postagem'}
            className={cn('flex items-center gap-1.5 text-xs font-bold transition-colors', post.pinned ? 'text-mission' : 'text-neutral-dark')}
          >
            {post.pinned ? <MdPushPin className="h-4 w-4" /> : <MdOutlinePushPin className="h-4 w-4" />}
          </button>
        )}

        {session?.user?.id === post?.['user-id'] && (
          <button
            type="button"
            onClick={deletePost}
            aria-label="Excluir postagem"
            className="ml-auto flex items-center text-neutral-dark transition-colors hover:text-danger"
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {commentsOpen && (
        <div className="flex flex-col gap-2 border-t border-neutral-lightest pt-2">
          {loadingComments && <Loader />}

          {!loadingComments && comments?.length > 0 && (
            <div className="flex max-h-80 flex-col gap-2 overflow-y-auto">
              {comments.map(comment => (
                <div
                  key={comment.id}
                  className={cn('flex items-start justify-between gap-2 rounded-xl bg-bright px-3 py-2 transition-opacity', deletingCommentId === comment.id && 'pointer-events-none opacity-50')}
                >
                  <div className="min-w-0 grow">
                    <p className="mb-0 text-xs font-bold text-neutral-900">{comment?.user?.name}</p>
                    <p className="mb-0 break-words text-sm text-neutral-dark">{comment.body}</p>
                  </div>

                  {session?.user?.id === comment?.user?.id && (
                    <button
                      type="button"
                      onClick={() => deleteComment(comment)}
                      disabled={deletingCommentId === comment.id}
                      aria-label="Excluir comentário"
                      className="shrink-0 text-neutral-dark transition-colors hover:text-danger"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <form
            onSubmit={submitComment}
            className="flex gap-2"
          >
            <input
              type="text"
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              placeholder="Escreva um comentário..."
              aria-label="Escreva um comentário"
              className="min-w-0 grow rounded-full border border-neutral-lighter px-3 py-1.5 text-xs"
            />
            <button
              type="submit"
              className="shrink-0 rounded-full px-3 py-1.5 text-xs font-extrabold text-white"
              style={{ background: 'var(--color-mission)' }}
            >
              Enviar
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
